import React, { Children, ReactElement, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import {
  CollapsibleContent,
  commonUtils,
  responsiveUtils,
  ScrollBarWrapper,
} from '@sato/components';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';

import { SectionTitle } from '@components/SectionTitle';
import { localizationStore } from '@stores/localization';

import { ResolveEntry } from '../../components';
import { IGrid, IGridFields, ParsedEntry } from '../../types';
import { includesEntry } from '../../utils/entry.utils';

interface Props extends ParsedEntry<IGrid> {
  className?: string;
  children?: ReactElement | ReactElement[];
}

const alignItemsMap: Record<IGridFields['align'], string> = {
  center: 'items-center',
  left: 'items-start',
  right: 'items-end',
};

const justifyItemsMap: Record<IGridFields['align'], string> = {
  center: 'justify-items-center',
  left: 'justify-items-start',
  right: 'justify-items-end',
};

export const Grid = observer(
  ({
    title,
    columns = 2,
    columnsTablet,
    columnsMobile,
    variant = 'responsive',
    content,
    id,
    children: childrenProp,
    className,
    previewedEntries,
    includeGaps = true,
    backgroundColor,
    align = 'left',
    isCard = false,
    autoCollapse,
    titleLinkText,
    titleLinkUrl,
    itemGapDesktop,
    itemGapTablet,
    itemGapMobile,
    titleBottomBorder,
  }: Props) => {
    const language = localizationStore.language;
    const t = localizationStore.getTranslation;
    const { hash } = useLocation();

    const children = useMemo(
      () =>
        childrenProp ??
        content
          ?.map((entry) => {
            const element = <ResolveEntry key={entry.id} entry={entry} />;
            if (!element) return null;
            return <React.Fragment key={entry.id}>{element}</React.Fragment>;
          })
          .filter(commonUtils.isTruthy),
      [childrenProp, content, language]
    );

    /** Grid gap defined in Contentful for different screen sizes */
    const customGridGap = (() => {
      if (responsiveUtils.screenSizeExceedsBreakpoint.lg) {
        return itemGapDesktop;
      } else if (responsiveUtils.screenSizeExceedsBreakpoint.sm) {
        return itemGapTablet;
      }
      return itemGapMobile;
    })();

    // render section only if it has content
    if (!Children.toArray(children).length) return null;

    const focusedEntryId = hash.slice(1); // drop leading "#" character
    const shouldOpen =
      content &&
      (id === focusedEntryId || includesEntry(content, focusedEntryId));

    return (
      <div
        id={id}
        className={classNames(
          'flex w-full flex-col',
          alignItemsMap[align],
          isCard && 'rounded-md px-3 py-6'
        )}
        style={{
          backgroundColor: backgroundColor?.color,
        }}
      >
        {(title || (titleLinkText && titleLinkUrl)) && (
          <>
            <SectionTitle
              buttonProps={
                titleLinkText && titleLinkUrl
                  ? { children: titleLinkText, to: titleLinkUrl }
                  : undefined
              }
              className="w-full"
              bottomBorder={titleBottomBorder}
            >
              {title}
            </SectionTitle>

            {backgroundColor && (
              <div className={classNames('h-[1px] w-full bg-black-light')} />
            )}
          </>
        )}

        {variant === 'scrollable' ? (
          <ScrollBarWrapper
            variant="horizontal"
            className={classNames('flex-nowrap pb-4', className)}
            columns={previewedEntries ?? columns}
            columnMinWidth={327}
            style={{ gap: customGridGap }}
          >
            {children}
          </ScrollBarWrapper>
        ) : (
          <CollapsibleContent
            showMoreLabel={t('common.ui.showMore')}
            showLessLabel={t('common.ui.showLess')}
            childrenLimit={previewedEntries}
            className={classNames(
              'w-full p-[2px]',
              includeGaps && 'gap-3 md:gap-5',
              gridStyles({ columns, columnsMobile, columnsTablet })[variant],
              variant !== 'responsive' && alignItemsMap[align],
              justifyItemsMap[align],
              className
            )}
            style={{ gap: customGridGap }}
            showFade={!backgroundColor?.color}
            autoCollapseNestedContent={autoCollapse}
            isOpen={shouldOpen}
            onOpenChange={(isOpen) => {
              if (!(isOpen && shouldOpen)) return;
              // scroll to content only after it is ready to be displayed (e.g. the accordion is opened)
              requestAnimationFrame(() =>
                window.document
                  .getElementById(focusedEntryId)
                  ?.scrollIntoView(true)
              );
            }}
          >
            {children}
          </CollapsibleContent>
        )}
      </div>
    );
  }
);

const gridStyles = ({
  columns,
  columnsTablet = Math.ceil(columns / 2),
  columnsMobile = 1,
}: {
  columns: number;
  columnsTablet?: number;
  columnsMobile?: number;
}) => ({
  responsive: classNames(
    `grid grid-cols-${columnsMobile} md:grid-cols-${columnsTablet} lg:grid-cols-${columns} items-start`
  ),
  column: 'flex flex-col',
  row: 'flex flex-col lg:flex-row *:flex-1',
});
